import { useMemo } from 'react';

const EMPTY_PREVIEW = {
  eff_cap_length_cm: '',
  capillary_volume_nl: '',
  injected_volume_nl: '',
  injection_fraction_pct: '',
  predicted_t0_s: '',
};

export function useExperimentalCalculations(fields = {}) {
  return useMemo(() => {
    const required = [
      'temperature_c',
      'viscosity_mpa_s',
      'total_length_cm',
      'post_detector_length_cm',
      'capillary_diam_um',
      'injection_pressure_mbar',
      'injection_time_s',
      'mobilizing_pressure_mbar',
    ];

    const parsed = {};
    for (const key of required) {
      const raw = fields[key];
      const num = Number(raw);
      if (raw === '' || raw === null || raw === undefined || Number.isNaN(num)) {
        return { values: { ...EMPTY_PREVIEW }, warnings: [] };
      }
      parsed[key] = num;
    }

    const eff_cap_length_cm =
      parsed.total_length_cm - parsed.post_detector_length_cm;
    if (!Number.isFinite(eff_cap_length_cm) || eff_cap_length_cm <= 0) {
      return {
        values: { ...EMPTY_PREVIEW },
        warnings: ['Effective capillary length must be positive.'],
      };
    }

    const eff_cap_length_m = eff_cap_length_cm / 100;
    const viscosity_pa_s = parsed.viscosity_mpa_s / 1000;
    const total_length_m = parsed.total_length_cm / 100;
    const rc_m = (parsed.capillary_diam_um * 1e-6) / 2;
    const capillary_volume_m3 = Math.PI * rc_m ** 2 * eff_cap_length_m;

    const injectionDenominator = 8 * viscosity_pa_s * eff_cap_length_m;
    let injected_volume_m3 =
      injectionDenominator === 0
        ? 0
        : (Math.PI *
            rc_m ** 4 *
            parsed.injection_pressure_mbar *
            100 *
            parsed.injection_time_s) /
          injectionDenominator;
    if (!Number.isFinite(injected_volume_m3)) {
      injected_volume_m3 = 0;
    }

    let injection_fraction_pct =
      capillary_volume_m3 === 0
        ? 0
        : (injected_volume_m3 / capillary_volume_m3) * 100;
    if (!Number.isFinite(injection_fraction_pct)) {
      injection_fraction_pct = 0;
    }

    const U =
      (parsed.mobilizing_pressure_mbar * 100 * rc_m ** 2) /
      (8 * viscosity_pa_s * total_length_m);
    let predicted_t0_s = U === 0 ? 0 : eff_cap_length_m / U;
    if (!Number.isFinite(predicted_t0_s)) {
      predicted_t0_s = 0;
    }

    const warnings = [];
    if (injection_fraction_pct > 1) {
      warnings.push('Injection fraction above 1%.');
    }
    if (injection_fraction_pct < 0.01) {
      warnings.push('Injection fraction below 0.01%.');
    }
    if (predicted_t0_s < 1) {
      warnings.push('Predicted t0 below 1 s.');
    }
    if (predicted_t0_s > 1000) {
      warnings.push('Predicted t0 above 1000 s.');
    }
    const temperature_c = parsed.temperature_c;
    if (temperature_c < 15 || temperature_c > 40) {
      warnings.push(
        `Temperature ${temperature_c.toFixed(2)} °C outside 15-40 °C range.`
      );
    }

    return {
      values: {
        eff_cap_length_cm,
        capillary_volume_nl: capillary_volume_m3 * 1e12,
        injected_volume_nl: injected_volume_m3 * 1e12,
        injection_fraction_pct,
        predicted_t0_s,
      },
      warnings,
    };
  }, [fields]);
}
